import React, { useContext, Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import { Layout } from './components/Layout';
import AuthContext from './context/AuthContext';
import { IContext } from './types/context';
import ChatWidgetManager from './components/ChatWidgetManger';
import { useMsal } from '@azure/msal-react';
import { logout as logoutApi } from './services/opw.api';

// Lazy load components
const NotFound = lazy(() => import('./pages/404/NotFound'));
const WarrantyPlans = lazy(() => import('./pages/WarrantyPlans'));
const PlanDetails = lazy(() => import('./pages/WarrantyPlans/PlanDetails'));
const Login = lazy(() => import('./pages/auth/login'));
const Register = lazy(() => import('./pages/auth/register'));
const Credentials = lazy(() => import('./pages/auth/credentails'));
const ClaimDetail = lazy(() => import('./pages/claim/details'));
const ClaimDetailPub = lazy(() => import('./pages/claim/detailsPub'));
const ClaimList = lazy(() => import('./pages/claim/list'));
const Dashboard = lazy(() => import('./pages/dashboard'));
const FileClaim = lazy(() => import('./pages/claim/fileClaim'));
const ProductCreate = lazy(() => import('./pages/products/create'));
const Product = lazy(() => import('./pages/products'));

export const handleLogOut = async (authContext: IContext, msalInstance?: any) => {
  // opw-service does have a backend logout endpoint (POST /auth/logout) that
  // should be invoked to invalidate the session/token server-side. This is
  // best-effort/non-blocking: if it fails (network issue, already-expired
  // token, etc.) we still proceed to clear client-side state and redirect,
  // since the user's intent to log out must always succeed locally.
  const token = authContext?.data?.token;
  if (token) {
    try {
      await logoutApi(token);
    } catch (error: any) {
      console.log('error calling backend logout', error?.message);
    }
  }

  if (msalInstance) {
    try {
      msalInstance.setActiveAccount(null);
    } catch (error: any) {
      console.log('error while clearing account', error?.message);
    }
  }

  authContext?.logout();
};

function App() {
  const authContext = useContext(AuthContext);

  const isLoggedIn = authContext?.data?.token && authContext?.data?.user ? true : false;

  return (
    <div className='App' style={{ height: '100vh', overflow: 'hidden' }}>
      <Suspense fallback={<div>Loading...</div>}>
        <ChatWidgetManager />
        <Routes>
          <Route path='/' element={<Layout withSidebar={isLoggedIn} />}>
            <Route path='/' element={<Dashboard />} />
            <Route path='/claim' element={<ClaimList />} />
            <Route path='/claim/:id' element={isLoggedIn ? <ClaimDetail /> : <ClaimDetailPub />} />
            <Route path='/claim/create' element={<FileClaim />} />
            <Route path='/warrantyplans' element={<WarrantyPlans />} />
            <Route path='/warrantyplans/:id' element={<PlanDetails />} />
            <Route path='/products' element={<Product />} />
            <Route path='/products/create' element={<ProductCreate />} />
            <Route path='*' element={<NotFound />} />
          </Route>
          <Route path='/login' element={<Login />} />
          <Route path='/register' element={<Register />} />
          <Route path='/credentials' element={<Credentials />} />
        </Routes>
      </Suspense>
    </div>
  );
}

export default App;
